import React from 'react';
import { Card, Field, TextField, CssColorField, SaveButton } from '../AdminUI';
import { useSite } from '../../../context/SiteContext';
import { toast } from 'sonner';
import MediaUpload from '../MediaUpload';

export default function PromotionsSection() {
  const { data, update } = useSite();
  const [promo, setPromo] = React.useState(data.promo || { title: '', gradient: '', link: '' });
  const [features, setFeatures] = React.useState(data.features || []);
  const [whatsapp, setWhatsapp] = React.useState(data.whatsappLink);
  const [social, setSocial] = React.useState(data.social || { whatsapp: '', telegram: '', instagram: '', youtube: '' });

  // Sync from server data only on mount; otherwise live polling would
  // overwrite the admin's in-progress edits (e.g. a freshly uploaded video).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    setPromo(data.promo || { title: '', gradient: '', link: '' });
    setFeatures(data.features || []);
    setWhatsapp(data.whatsappLink);
    setSocial(data.social || { whatsapp: '', telegram: '', instagram: '', youtube: '' });
  }, []);

  const save = () => {
    update((prev) => ({ ...prev, promo, features, whatsappLink: whatsapp, social }));
    toast.success('Saved successfully');
  };

  const setFeat = (i, k, v) => setFeatures(features.map((f, idx) => idx === i ? { ...f, [k]: v } : f));

  return (
    <>
      <Card title="Promotions Banner" desc="The bright call-to-action banner on the homepage. Supports background image/GIF/video — videos autoplay." footer={<SaveButton onClick={save} />}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Title"><TextField value={promo.title} onChange={(v) => setPromo({ ...promo, title: v })} /></Field>
          <Field label="Link (URL or #anchor)"><TextField value={promo.link} onChange={(v) => setPromo({ ...promo, link: v })} /></Field>
          <div className="md:col-span-2">
            <label className="text-xs font-semibold text-zinc-300 mb-1.5 block">Logo / Icon (image — shown on the left)</label>
            <MediaUpload value={promo.logo || ''} onChange={(v) => setPromo({ ...promo, logo: v })} accept="image" previewClass="h-12 w-12" testid="promo-logo" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-semibold text-zinc-300 mb-1.5 block">Banner Background Media (image / GIF / video — replaces gradient if set)</label>
            <MediaUpload value={promo.media || ''} onChange={(v) => setPromo({ ...promo, media: v })} accept="media" previewClass="h-16 w-32" testid="promo-media" />
          </div>
          <div className="md:col-span-2">
            <Field label="Gradient / Background CSS (fallback when no media)">
              <CssColorField value={promo.gradient} onChange={(v) => setPromo({ ...promo, gradient: v })} placeholder="linear-gradient(...)" />
            </Field>
          </div>
        </div>
      </Card>

      <Card title="Feature Cards" desc="Book a Meeting / WhatsApp Chat / etc — each card supports image, GIF or video + click link." footer={<SaveButton onClick={save} />}>
        <div className="space-y-3">
          {features.map((f, i) => {
            const isEnabled = f.enabled !== false;
            return (
              <div key={f.id} className={`rounded-lg border p-3 transition ${isEnabled ? 'border-zinc-800 bg-zinc-900/40' : 'border-zinc-800/40 bg-zinc-950/60 opacity-60'}`} data-testid={`feature-card-${i}`}>
                {/* Enable / Disable header row */}
                <div className="flex items-center justify-between mb-3 pb-2 border-b border-zinc-800">
                  <span className="text-xs font-bold text-zinc-300 truncate max-w-[60%]">{f.label || `Card ${i + 1}`}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isEnabled ? 'bg-green-900/40 text-green-400' : 'bg-red-900/40 text-red-400'}`}>
                      {isEnabled ? 'ENABLED' : 'DISABLED'}
                    </span>
                    <button
                      onClick={() => setFeat(i, 'enabled', !isEnabled)}
                      className={`h-7 px-3 rounded-md text-xs font-bold border transition active:scale-95 ${isEnabled
                        ? 'bg-red-900/20 hover:bg-red-900/40 border-red-900/50 text-red-400'
                        : 'bg-green-900/20 hover:bg-green-900/40 border-green-900/50 text-green-400'
                      }`}
                      data-testid={`feature-card-toggle-${i}`}
                    >
                      {isEnabled ? 'Disable' : 'Enable'}
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Label"><TextField value={f.label} onChange={(v) => setFeat(i, 'label', v)} /></Field>
                  <Field label="Subtitle"><TextField value={f.sub} onChange={(v) => setFeat(i, 'sub', v)} /></Field>
                  <Field label="Icon (lucide name)"><TextField value={f.icon} onChange={(v) => setFeat(i, 'icon', v)} /></Field>
                  <Field label="Background (CSS gradient / color)">
                    <CssColorField value={f.bg} onChange={(v) => setFeat(i, 'bg', v)} placeholder="linear-gradient(...)" />
                  </Field>
                  <Field label="Link (URL or #anchor)"><TextField value={f.link || ''} onChange={(v) => setFeat(i, 'link', v)} placeholder="https://… or #section" /></Field>
                  <div>
                    <label className="text-xs font-semibold text-zinc-300 mb-1.5 block">Media (logo / GIF / video — replaces lucide icon)</label>
                    <MediaUpload value={f.image || ''} onChange={(v) => setFeat(i, 'image', v)} accept="media" previewClass="h-12 w-12" testid={`feature-media-${i}`} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <Card title="Social Media & WhatsApp" desc="Floating WhatsApp button + footer social icons." footer={<SaveButton onClick={save} />}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Floating WhatsApp Link" hint="https://wa.me/919999999999">
            <TextField value={whatsapp} onChange={setWhatsapp} />
          </Field>
          <Field label="WhatsApp (footer)"><TextField value={social.whatsapp} onChange={(v) => setSocial({ ...social, whatsapp: v })} /></Field>
          <Field label="Telegram"><TextField value={social.telegram} onChange={(v) => setSocial({ ...social, telegram: v })} /></Field>
          <Field label="Instagram"><TextField value={social.instagram} onChange={(v) => setSocial({ ...social, instagram: v })} /></Field>
          <Field label="YouTube"><TextField value={social.youtube} onChange={(v) => setSocial({ ...social, youtube: v })} /></Field>
        </div>
      </Card>
    </>
  );
}
